import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { ReportType, ReportFormData, UserRole } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';

interface FormData {
  title: string;
  type: ReportType;
  content: string;
  date: any;
  hourlySummary?: string;
  workSummary?: string;
  totalHours?: number;
  hourlyRate?: number;
}

interface ReportCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ReportFormData) => void;
  selectedType?: 'hourly' | 'work' | 'weekly' | 'user' | 'external' | 'summary' | 'call' | 'risk' | 'livechat';
}

const ReportCreateModal: React.FC<ReportCreateModalProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  selectedType: selectedReportType 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const { user } = useAuth();
  const { settings } = useSettings();

  // Seçilen türe göre rapor türünü belirle
  const getReportTypeFromSelection = React.useCallback((): ReportType => {
    switch (selectedReportType) {
      case 'hourly':
        return ReportType.HOURLY;
      case 'work':
        return ReportType.WORK_SUMMARY;
      case 'weekly':
        return ReportType.WEEKLY;
      case 'user':
        return ReportType.USER_REPORT;
      case 'external':
        return ReportType.EXTERNAL_REPORT;
      case 'summary':
        return ReportType.WEEKLY_SUMMARY;
      case 'call':
      case 'risk':
      case 'livechat':
        return ReportType.DAILY; // Yeni rapor türleri için geçici olarak DAILY kullanıyoruz
      default:
        return ReportType.DAILY;
    }
  }, [selectedReportType]);

  // Seçilen türe göre başlık belirle
  const getTitleFromSelection = React.useCallback((): string => {
    switch (selectedReportType) {
      case 'hourly':
        return 'Saatlik Rapor';
      case 'work':
        return 'Mesai Raporu';
      case 'weekly':
        return 'Haftalık Rapor';
      case 'user':
        return 'Kullanıcı Raporu';
      case 'external':
        return 'Dış Rapor';
      case 'summary':
        return 'Haftalık Özet Raporu';
      case 'call':
        return 'Call Raporu';
      case 'risk':
        return 'Risk Raporu';
      case 'livechat':
        return 'Canlı Chat Raporu';
      default:
        return 'Günlük Rapor';
    }
  }, [selectedReportType]);

  // Ayarlara göre dinamik form alanları oluştur
  const getDynamicFields = React.useCallback((): Array<{
    name: string;
    label: string;
    required: boolean;
    type: string;
  }> => {
    if (!selectedReportType || !['call', 'risk', 'livechat'].includes(selectedReportType)) {
      return [];
    }

    const reportSettings = settings[`${selectedReportType}ReportSettings` as keyof typeof settings] as any;
    if (!reportSettings) return [];

    const fields: Array<{
      name: string;
      label: string;
      required: boolean;
      type: string;
    }> = [];
    
    // Zorunlu alanlar
    reportSettings.requiredFields.forEach((field: string) => {
      fields.push({
        name: field,
        label: field.charAt(0).toUpperCase() + field.slice(1),
        required: true,
        type: 'text'
      });
    });

    // Opsiyonel alanlar
    reportSettings.optionalFields.forEach((field: string) => {
      fields.push({
        name: field,
        label: field.charAt(0).toUpperCase() + field.slice(1),
        required: false,
        type: 'text'
      });
    });

    // Özel alanlar
    reportSettings.customFields.forEach((field: any) => {
      fields.push({
        name: field.name,
        label: field.name,
        required: field.required,
        type: field.type
      });
    });

    return fields;
  }, [selectedReportType, settings]);


  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      title: '',
      type: getReportTypeFromSelection(),
      content: '',
      date: dayjs(),
      hourlySummary: '',
      workSummary: '',
      totalHours: 0,
      hourlyRate: 0
    }
  });

  // Seçilen tür değiştiğinde form değerlerini güncelle
  React.useEffect(() => {
    if (selectedReportType) {
      setValue('type', getReportTypeFromSelection());
      setValue('title', getTitleFromSelection());
    }
  }, [selectedReportType, setValue, getReportTypeFromSelection, getTitleFromSelection]);

  const isRiskUser = user?.role === UserRole.RISK || user?.role === UserRole.RISK_ADMIN;

  const handleFormSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      setIsSubmitting(true);
      setError('');
      
      // Simüle edilmiş API çağrısı
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const reportData: ReportFormData = {
        title: data.title,
        type: data.type,
        content: data.content,
        date: data.date,
        hourlySummary: data.hourlySummary,
        workSummary: data.workSummary,
        totalHours: data.totalHours,
        hourlyRate: data.hourlyRate
      };
      
      onSubmit(reportData);
      reset();
      onClose();
    } catch (err) {
      setError('Rapor oluşturulurken hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setError('');
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '500px' }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Yeni Rapor Oluştur
          </Typography>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" noValidate>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Rapor Başlığı"
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    placeholder={getTitleFromSelection()}
                  />
                )}
              />

              <Controller
                name="date"
                control={control}
                render={({ field }) => {
                  // Saatlik rapor için DateTimePicker, diğerleri için DatePicker
                  const isHourlyReport = selectedReportType === 'hourly';
                  
                  if (isHourlyReport) {
                    return (
                      <DateTimePicker
                        {...field}
                        label="Rapor Tarihi ve Saati"
                        value={field.value}
                        onChange={field.onChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.date,
                            helperText: errors.date?.message as string
                          }
                        }}
                      />
                    );
                  }
                  
                  return (
                    <DatePicker
                      {...field}
                      label="Rapor Tarihi"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.date,
                          helperText: errors.date?.message as string
                        }
                      }}
                    />
                  );
                }}
              />

              {/* Dinamik alanlar - Call, Risk, LiveChat raporları için */}
              {getDynamicFields().map((field) => (
                <Controller
                  key={field.name}
                  name={field.name as any}
                  control={control}
                  render={({ field: controllerField }) => (
                    <TextField
                      {...controllerField}
                      fullWidth
                      label={field.label}
                      required={field.required}
                      type={field.type === 'number' ? 'number' : 'text'}
                      multiline={field.type === 'text' && field.name.includes('notes')}
                      rows={field.type === 'text' && field.name.includes('notes') ? 3 : 1}
                      error={!!errors[field.name as keyof typeof errors]}
                      helperText={errors[field.name as keyof typeof errors]?.message as string}
                      placeholder={`${field.label} ${field.required ? '(zorunlu)' : '(opsiyonel)'}`}
                    />
                  )}
                />
              ))}

              {/* Genel içerik alanı - sadece dinamik alanlar yoksa göster */}
              {getDynamicFields().length === 0 && (
                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={6}
                      label="Rapor İçeriği"
                      error={!!errors.content}
                      helperText={errors.content?.message}
                      placeholder="Rapor detaylarını buraya yazın..."
                    />
                  )}
                />
              )}

              {/* Risk kullanıcıları için özel alanlar */}
              {isRiskUser && (
                <>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Risk Raporu Detayları
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 3 
                  }}>
                    <Controller
                      name="totalHours"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Toplam Çalışma Saati"
                          error={!!errors.totalHours}
                          helperText={errors.totalHours?.message}
                          placeholder="8"
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      )}
                    />

                    <Controller
                      name="hourlyRate"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Saatlik Ücret (TL)"
                          error={!!errors.hourlyRate}
                          helperText={errors.hourlyRate?.message}
                          placeholder="50"
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      )}
                    />
                  </Box>

                  <Controller
                    name="hourlySummary"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={3}
                        label="Saatlik Özet"
                        error={!!errors.hourlySummary}
                        helperText={errors.hourlySummary?.message}
                        placeholder="Saatlik olarak yapılan işlerin özetini yazın..."
                      />
                    )}
                  />

                  <Controller
                    name="workSummary"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={3}
                        label="Mesai Özeti"
                        error={!!errors.workSummary}
                        helperText={errors.workSummary?.message}
                        placeholder="Mesai süresince yapılan işlerin detaylı özetini yazın..."
                      />
                    )}
                  />
                </>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            İptal
          </Button>
          <Button
            onClick={handleSubmit(handleFormSubmit)}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Oluşturuluyor...' : 'Rapor Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ReportCreateModal;
